import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';

import { alertService, AlertType } from '../services';

function Alert({ id='default-alert', fade = true }) {
  const router = useRouter();
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    // subscribe to new alert notifications
    const subscription = alertService.onAlert(id)
      .subscribe(alert => {
        // clear alerts when an empty alert is received
        if (!alert.message) {
          setAlerts(alerts => {
            // filter out alerts without 'keepAfterRouteChange' flag
            const filteredAlerts = alerts.filter(x => x.keepAfterRouteChange);

            // remove 'keepAfterRouteChange' flag on the rest
            filteredAlerts.forEach(x => delete x.keepAfterRouteChange);
            return filteredAlerts;
          });
        } else {
          // add alert to array
          setAlerts(alerts => ([...alerts, alert]));

          // auto close alert if required
          if (alert.autoClose) {
            setTimeout(() => removeAlert(alert), 3000);
          }
        }
      });


    // clear alerts on location change
    const onRouteChange = () => alertService.clear(id);
    router.events.on('routeChangeStart', onRouteChange);

    // clean up function that runs when the component unmounts
    return () => {
      // unsubscribe to avoid memory leaks
      subscription.unsubscribe();
      router.events.off('routeChangeStart', onRouteChange);
    };
  }, []);

  function removeAlert(alert) {
    if (fade) {
      // fade out alert
      const alertWithFade = { ...alert, fade: true };
      setAlerts(alerts => alerts.map(x => x === alert ? alertWithFade : x));

      // remove alert after faded out
      setTimeout(() => {
        setAlerts(alerts => alerts.filter(x => x !== alertWithFade));
      }, 250);
    } else {
      // remove alert
      setAlerts(alerts => alerts.filter(x => x !== alert));
    }
  }

  function cssClasses(alert) {
    if (!alert) return;

    const classes = ['alert', 'alert-dismissable'];

    const alertTypeClass = {
      [AlertType.Success]: 'alert-success',
      [AlertType.Error]: 'alert-danger',
      [AlertType.Info]: 'alert-info',
      [AlertType.Warning]: 'alert-warning'
    }

    classes.push(alertTypeClass[alert.type]);

    if (alert.fade) {
      classes.push('fade');
    }

    return classes.join(' ');
  }

  if (!alerts.length) return null;

  return (
    <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 9999 }}>
      {alerts.map((alert, index) => (
        <div 
          key={index} 
          className={`toast show align-items-center text-white border-0 mb-2 ${alertTypeClass[alert.type]}`} 
          role="alert" 
          aria-live="assertive" 
          aria-atomic="true"
          style={{ minWidth: '250px', boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.15)' }}
        >
          <div className="d-flex">
            <div className="toast-body p-3">
               <span dangerouslySetInnerHTML={{ __html: alert.message }}></span>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-white me-2 m-auto shadow-none" 
              onClick={() => removeAlert(alert)}
              aria-label="Close"
            ></button>
          </div>
        </div>
      ))}
    </div>
  );
}

const alertTypeClass = {
    [AlertType.Success]: 'bg-success',
    [AlertType.Error]: 'bg-danger',
    [AlertType.Info]: 'bg-primary',
    [AlertType.Warning]: 'bg-warning text-dark'
}

export { Alert };